// modules/approvals/components/ApproverBeneficiaryCard.jsx
//
// Who the approver is about to pay, shown above the UTR entry on the Approve modal.
//
// The approver IS the person making the transfer — they are asked for a UTR because
// the money moves by their hand. Making them find the payee's account number on
// another screen before they can do that is how a transfer ends up in the wrong
// account, so the payee and their bank details are put in front of them here, read
// live off the case (not a snapshot) by GET /approval/decision-form.
//
// Layout rules, because this panel is read while money is being moved:
//   - an account number NEVER wraps. A number broken across two lines is misread,
//     and misreading it is the failure this panel exists to prevent. Each field is
//     a fixed no-shrink column; if the row genuinely cannot fit, it scrolls
//     sideways rather than folding a digit string onto a second line.
//   - the numbers are tabular-figure monospace, so digits line up and a repeated
//     0/O or 1/l is distinguishable at a glance.
//   - every number is one click to copy, so it is pasted into net-banking, not
//     re-typed from the screen.
//
// A pledge advance paid to the LENDER closes a loan account rather than paying the
// customer, so it shows the lender and the loan A/c No. instead of a bank account.

import React from 'react';
import { Typography, Tag, Alert } from 'antd';
import { UserOutlined, BankOutlined, PhoneOutlined, FileTextOutlined } from '@ant-design/icons';

const { Text } = Typography;

const MONO = "'SFMono-Regular', 'Roboto Mono', Menlo, Consolas, monospace";

const has = (v) => !!(v && String(v).trim());

/**
 * One labelled field in the details strip. Fixed width, never shrinks, never wraps —
 * the strip scrolls before any single value is allowed to fold onto a second line.
 */
const Field = ({ label, value, mono = false, copy = false, minWidth = 150, grow = false }) => (
  <div
    style={{
      flex: grow ? '1 1 auto' : '0 0 auto',
      minWidth,
      padding: '8px 14px',
      borderLeft: '1px solid #f0f0f0',
      whiteSpace: 'nowrap',
    }}
  >
    <div
      style={{
        fontSize: 10,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        color: '#8c8c8c',
        marginBottom: 2,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
    {has(value) ? (
      <Text
        strong
        copyable={copy ? { text: String(value) } : false}
        style={{
          fontSize: 13,
          whiteSpace: 'nowrap',
          fontFamily: mono ? MONO : undefined,
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
          letterSpacing: mono ? 0.3 : undefined,
        }}
      >
        {value}
      </Text>
    ) : (
      <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
    )}
  </div>
);

const ApproverBeneficiaryCard = ({ data, hasBankPortion = false }) => {
  if (!data) return null;

  const toLender = data.payTo === 'LENDER';
  // Bank details only matter when money is actually going out through a bank.
  const bankDetailsMissing = hasBankPortion && !toLender && !has(data.bankAccountNumber);

  return (
    <div
      style={{
        marginBottom: 14,
        border: '1px solid #d6e4ff',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      {/* ── Who ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '10px 14px',
          background: '#f5f8ff',
          borderBottom: '1px solid #eef2fb',
        }}
      >
        <UserOutlined style={{ color: '#2f54eb' }} />
        <Text strong style={{ fontSize: 15 }}>{has(data.name) ? data.name : '—'}</Text>
        <Tag color={toLender ? 'purple' : 'blue'} style={{ margin: 0 }}>
          {toLender ? 'Lender' : 'Customer'}
        </Tag>

        {/* Case reference sits at the far end — identification, not an instruction. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {has(data.phone) && !toLender && (
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {data.phone}
            </Text>
          )}
          {has(data.caseReference) && (
            <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              <FileTextOutlined style={{ margin: '0 4px 0 8px' }} />
              {data.caseReference}
              {has(data.caseType) ? ` · ${data.caseType}` : ''}
            </Text>
          )}
        </div>
      </div>

      {/* ── Where the money goes ── One row, no wrapping; scrolls if it must. ── */}
      <div style={{ display: 'flex', alignItems: 'stretch', overflowX: 'auto' }}>
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            color: '#595959',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          <BankOutlined style={{ color: '#2f54eb' }} />
          {toLender ? 'Loan account' : 'Bank details'}
        </div>

        {toLender ? (
          <>
            <Field label="Loan A/c No." value={data.loanAccountNumber} mono copy minWidth={180} />
            <Field label="Lender" value={data.lenderName || data.name} minWidth={160} />
            <Field label="Customer" value={data.customerName} minWidth={160} grow />
          </>
        ) : (
          <>
            <Field label="Bank A/c No." value={data.bankAccountNumber} mono copy minWidth={190} />
            <Field label="IFSC" value={data.ifscCode} mono copy minWidth={140} />
            <Field label="Branch" value={data.bankBranchName} minWidth={160} grow />
          </>
        )}
      </div>

      {bankDetailsMissing && (
        <Alert
          type="warning"
          showIcon
          banner
          style={{ borderTop: '1px solid #ffe58f' }}
          message="No bank account on file — part of this payment settles through a bank. Confirm the account with the branch before releasing it."
        />
      )}
    </div>
  );
};

export default ApproverBeneficiaryCard;
